import { escapeHTML } from '../../utils/escape.js';
import { formatFechaHora } from '../../utils/formatters.js';

/**
 * Diálogo de edición/cierre de una fichada (Control de Horarios y
 * Liquidación de Sueldos, slice 3 — Auditoría). Función de render pura,
 * mismo patrón que `renderPinDialog` en `EmpleadosView.js`: `js/admin.js`
 * (wiring) decide CUÁNDO mostrarlo y con qué datos, este componente solo
 * dibuja.
 *
 * `motivo` (Requirement: Admin Fichada Create, Edit, and Close — "Edit
 * without a reason is rejected") SIEMPRE es obligatorio: el
 * `<textarea required>` bloquea el submit nativo antes de que cualquier
 * listener corra. `admin-fichadas.service.js` vuelve a validarlo
 * server-side — esto es solo UX.
 *
 * `accion:'cerrar'` (Req: "Admin closes an open shift with a reason") solo
 * pide `salida_at` — `entrada_at` no se toca al cerrar una jornada abierta.
 * `accion:'editar'` pide ambos horarios, prellenados con los valores
 * actuales de la fichada.
 *
 * `accion:'anular'` (F-D, owner decision 2026-09-27) es el paso de
 * confirmación: muestra la fichada que se va a anular, explica que no se
 * borra (queda tachada y deja de contar) y solo pide el motivo — sin campos
 * de horario. El botón dice "Anular fichada", nunca un "Guardar" genérico.
 */

const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

/**
 * Convierte un timestamp ISO a `datetime-local` (`YYYY-MM-DDTHH:mm`), en hora
 * LOCAL del navegador — es solo para prellenar un `<input type="datetime-local">`
 * editable, no para ningún cálculo de política (eso vive en
 * timesheet-policy.js con su propia aritmética ART).
 * @param {string | null | undefined} iso
 * @returns {string}
 */
function toDatetimeLocalValue(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Títulos por acción; `editar` es el default (mismo criterio que antes de F-D). */
const TITULOS = { editar: 'Editar fichada', cerrar: 'Cerrar jornada', anular: 'Anular fichada' };

/**
 * @param {{ fichadaId?: string, accion?: 'editar'|'cerrar'|'anular', empleadoNombre?: string, entrada_at?: string|null, salida_at?: string|null, error?: string|null, aviso?: string|null }|null} [params]
 *   `aviso` (R5): un guardado que no respondió a tiempo y pudo haberse aplicado — se muestra como advertencia, no como error.
 * @returns {string} HTML del diálogo, o `''` si no hay `fichadaId` (diálogo cerrado).
 */
export function FichadaEditDialog(params) {
    const { fichadaId, accion, empleadoNombre, entrada_at, salida_at, error, aviso } = params ?? {};
    if (!fichadaId) return '';

    const isCerrar = accion === 'cerrar';
    const isAnular = accion === 'anular';
    const nombre = escapeHTML(empleadoNombre ?? '');
    const title = `${TITULOS[accion] ?? TITULOS.editar} — ${nombre}`;
    const errorHtml = error
        ? `<p class="text-xs text-red-500 font-bold mt-3" role="alert">${escapeHTML(error)}</p>`
        : '';
    const avisoHtml = aviso
        ? `<p class="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2 font-bold mt-3" role="status" data-fichada-dialog-aviso>${escapeHTML(aviso)}</p>`
        : '';
    const entradaFieldHtml = isCerrar || isAnular
        ? ''
        : `
                    <div class="mb-4">
                        <label class="${LABEL}">Entrada</label>
                        <input type="datetime-local" name="entrada_at" value="${escapeHTML(toDatetimeLocalValue(entrada_at))}" class="${FIELD}" />
                    </div>`;

    return `
        <div role="dialog" aria-modal="true" aria-labelledby="fichada-dialog-title" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-6">
            <div class="bg-base rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
                <p id="fichada-dialog-title" class="text-sm font-bold text-text-primary text-center mb-6">${title}</p>
                <form data-id="${escapeHTML(fichadaId)}" data-accion="${escapeHTML(accion ?? '')}">
                    ${entradaFieldHtml}
                    ${isAnular ? renderAnularResumen(entrada_at, salida_at) : renderSalidaField(salida_at)}
                    <div class="mb-2">
                        <label class="${LABEL}">Motivo</label>
                        <textarea name="motivo" required rows="3" class="${FIELD}"></textarea>
                    </div>
                    ${errorHtml}
                    ${avisoHtml}
                    <div class="flex gap-3 mt-6">
                        <button type="button" data-action="fichada-dialog-cancelar" class="flex-1 py-4 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">Cancelar</button>
                        <button type="submit" data-action="fichada-dialog-guardar" class="flex-1 py-4 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">${isAnular ? 'Anular fichada' : 'Guardar'}</button>
                    </div>
                </form>
            </div>
        </div>
    `;
}

/**
 * @param {string|null|undefined} salida_at
 * @returns {string}
 */
function renderSalidaField(salida_at) {
    return `
                    <div class="mb-4">
                        <label class="${LABEL}">Salida</label>
                        <input type="datetime-local" name="salida_at" value="${escapeHTML(toDatetimeLocalValue(salida_at))}" class="${FIELD}" />
                    </div>`;
}

/**
 * Paso de confirmación de `anular` (F-D): qué fichada es y qué pasa con ella.
 * @param {string|null|undefined} entrada_at
 * @param {string|null|undefined} salida_at
 * @returns {string}
 */
function renderAnularResumen(entrada_at, salida_at) {
    const rango = `${formatFechaHora(entrada_at)} → ${salida_at ? formatFechaHora(salida_at) : 'sin cierre'}`;
    return `
                    <div class="mb-4 text-sm text-text-primary">
                        <p class="font-bold mb-2">${escapeHTML(rango)}</p>
                        <p class="text-xs text-text-muted">No se borra: queda tachada en el listado y deja de contar para sueldos y anomalías. No se puede deshacer.</p>
                    </div>`;
}
